import * as THREE from 'three';
import { loft, place, paint, mergeAll, triCount } from './geom.js';

/**
 * ===========================================================================
 *  HORSE — procedural, like everything else
 * ===========================================================================
 *
 * DEV_PLAN Step 7, and the one item in the plan that is genuinely a build
 * rather than framing on work already done: nothing in this codebase had a
 * horse, a rider, or a mounted state. `ST.MOUNTED` was in the entity enum and
 * `KeyF:'mount'` was in the keymap, and neither was implemented by anything.
 *
 * Built with the same tool the people are built with — `loft()`, a swept
 * superellipse tube — so the horse shares their faceted, low-poly read rather
 * than arriving from a different art pipeline. Zero binary assets, as
 * CLAUDE.md requires.
 *
 * ---------------------------------------------------------------------------
 *  THE AXIS, written down because guessing it cost a capture
 * ---------------------------------------------------------------------------
 * `loft()` sweeps along +Y. A horse is a horizontal animal, so every volume
 * except the legs is lofted standing up and laid down with
 * `place({ rot: [+PI/2, 0, 0] })`. Three.js rotates about X as
 *
 *     y' = y·cosθ − z·sinθ ,   z' = y·sinθ + z·cosθ
 *
 * so at θ = +π/2 a local point (x, y, z) lands at world (x, −z, y). Read off
 * what that means for a ring, because this is the whole trick:
 *
 *     ring's  y   →  world  +Z   distance along the animal, tail → nose
 *     ring's  rx  →  world   x   HALF-WIDTH at that station
 *     ring's  rz  →  world  ∓y   HALF-HEIGHT at that station
 *                                (and LARGER local z is LOWER, which is why
 *                                 the belly shading below tests z, not y)
 *
 * The first build used θ = −π/2, which maps local +Y to −Z: the shoulder ended
 * up behind the croup and the animal was assembled inside out — tail out of
 * the chest, head hanging off the rump. It rendered without complaint and was
 * visibly a sausage with a stick in it. Only a frame said so.
 *
 * ---------------------------------------------------------------------------
 *  THE NECK, which the second attempt got wrong in a subtler way
 * ---------------------------------------------------------------------------
 * `tilt` here is measured FROM VERTICAL, not from horizontal, because that is
 * what `rot: [θ, 0, 0]` means for a part lofted up the +Y axis: θ = 0 leaves it
 * standing straight up. The second horse used 0.78 rad for the neck and 2.00
 * for the head, read as "45° up" and "115° over" — they are 45° and 115° off
 * VERTICAL, so the neck lay at 45° and the head carried on forward with a 24°
 * droop. Neck and head merged into one horizontal plank with a mane on it and
 * the animal read as a crocodile.
 *
 * A horse carries its neck steeply — around 55–60° above horizontal — and its
 * head hangs near 55° BELOW horizontal off the poll, so the two make a distinct
 * angle at the poll rather than a continuous tube. That angle, not the muzzle
 * detail, is what makes the silhouette read as a horse.
 *
 * ---------------------------------------------------------------------------
 *  PROPORTIONS
 * ---------------------------------------------------------------------------
 * Metres, against a 1.75 m man. Withers 1.55, belly 0.82 — so the body is
 * 0.73 deep and the legs are 0.82 long, which is roughly the real ratio and
 * the thing the second attempt got most visibly wrong (belly at 0.685 gave it
 * a deep barrel on stubby legs, i.e. a pig). Through the ribs it is 0.55 wide,
 * not 0.70: seen from the front a horse is a narrow animal.
 */

export const HORSE = {
  withers: 1.55,       // top of the shoulder, hooves on y = 0
  length: 2.20,        // nose to dock (the tail hangs past it)
  barrelR: 0.275,      // half-width through the ribs
  belly: 0.82,         // underline height — what the legs have to clear
  saddleZ: -0.10,      // the saddle's station along the back
  seatY: 1.54,         // WORLD height of the seat the rider sits on

  /**
   * What mount.js adds to the rider's mesh y.
   *
   * The rider's mesh origin is between his feet, so this is NOT the seat
   * height: it is the seat height minus how far his hips sit above that
   * origin while seated. rig.js binds `hips` at y = 0.980 and the `mounted`
   * clip in clips.js raises the root by ~0.38, which puts his seat 1.36 above
   * his own origin. 1.54 − 1.36 = 0.18.
   *
   * The first value shipped here was 1.32 — the seat height used directly —
   * and it stood the hero a metre and a third above his horse. The capture
   * logs the hips bone's world y against `seatY` for exactly this reason.
   */
  saddleY: 0.18,
  /** How far the rider's hips sit above his own mesh origin while seated. */
  riderHip: 1.36,
};

/* Where the barrel is placed. World z = BARREL_Z + local y; world y =
 * BARREL_Y − local z. Both are read by the landmark test. */
const BARREL_Z = -1.10;
const BARREL_Y = 1.185;   // (withers + belly) / 2
const BARREL_LEN = 1.60;

/* The neck, head and tail as AXES, hoisted out of the builder so the landmark
 * test can assert the angles directly. `tilt` is measured FROM VERTICAL — see
 * the note above; reading it as "from horizontal" is what produced the
 * crocodile. */
const NECK = { y: 1.34, z: 0.40, tilt: 0.62, len: 0.72 };
const HEAD = { tilt: 2.53, len: 0.52 };
const TAIL = { y: 1.34, z: -1.06, tilt: -2.42, len: 0.70 };

const POLL = [
  0,
  NECK.y + NECK.len * Math.cos(NECK.tilt),
  NECK.z + NECK.len * Math.sin(NECK.tilt),
];
const NOSE = [
  0,
  POLL[1] + HEAD.len * Math.cos(HEAD.tilt),
  POLL[2] + HEAD.len * Math.sin(HEAD.tilt),
];
const TAIL_TIP = [
  0,
  TAIL.y + TAIL.len * Math.cos(TAIL.tilt),
  TAIL.z + TAIL.len * Math.sin(TAIL.tilt),
];

/**
 * Named points and angles in the horse's own space, for `tools/horse-check.mjs`.
 *
 * The angles are the load-bearing entries. Both horses this step threw away
 * were wrong in the ANGLES and right everywhere else: the first laid the body
 * down with the wrong sign, the second carried the neck at 45° above
 * horizontal and dropped the head only 24° below it, so neck and head made one
 * flat plank and the animal read as a crocodile. A bounding box cannot see
 * either failure. These can.
 */
export const LANDMARKS = {
  poll: POLL, nose: NOSE, tailTip: TAIL_TIP,
  seat: [0, 1.54, -0.10],
  /** Degrees the neck rises above horizontal. A horse carries it 50–65°. */
  neckRise: Math.atan2(Math.cos(NECK.tilt), Math.sin(NECK.tilt)) * 180 / Math.PI,
  /** Degrees the head drops below horizontal off the poll. 45–70°. */
  headDrop: Math.atan2(-Math.cos(HEAD.tilt), Math.sin(HEAD.tilt)) * 180 / Math.PI,
  /** ...and the angle they make at the poll, which must be a real bend. */
  pollAngle: Math.acos(
    Math.cos(NECK.tilt) * Math.cos(HEAD.tilt) + Math.sin(NECK.tilt) * Math.sin(HEAD.tilt),
  ) * 180 / Math.PI,
};

/**
 * `paint(g, colour, fn)` takes a THREE.Color and its callback returns a
 * MULTIPLIER, not another colour — getting that wrong paints the whole animal
 * in whatever the first argument was and silently ignores the shading. Hence
 * Colors here and scalars in every `fn` below. `paint` also runs BEFORE
 * `place`, so every callback sees LOCAL coordinates.
 */
const C = {
  hide: new THREE.Color(0x6b4a33),      // bay
  mane: new THREE.Color(0x241a12),
  tack: new THREE.Color(0x7a2f22),      // Wilwatikta red leather
  blanket: new THREE.Color(0x8c3326),
};
/** Shading multipliers, so one hide colour reads as a lit animal. */
const K = {
  dark: 0.58,     // the black points: lower legs, muzzle, tail dock
  hoof: 0.26,
  eye: 0.14,
  belly: 0.70,    // underline in shadow
  back: 1.18,     // topline catching the sky
  blaze: 1.50,    // the face stripe, which is what names a horse at range
  trim: 1.55,
};

/** One leg, built at the origin and placed by the caller. */
function leg(front) {
  const top = front ? 0.98 : 1.03;
  const g = loft([
    { y: 0.00, rx: 0.078, rz: 0.088, sq: 2.4 },            // hoof
    { y: 0.10, rx: 0.052, rz: 0.060, sq: 2.2 },            // pastern
    { y: 0.38, rx: 0.046, rz: 0.052, sq: 2.1 },            // cannon
    { y: 0.58, rx: 0.072, rz: 0.086, sq: 2.2 },            // knee / hock
    { y: top,  rx: front ? 0.108 : 0.132, rz: 0.120, sq: 2.3 },  // forearm / gaskin
  ], { seg: 8, vFromY: true, capTop: true, capBottom: true });
  paint(g, C.hide, (x, y) => (y < 0.14 ? K.hoof : y < 0.56 ? K.dark : 1));
  return g;
}

/**
 * Build one horse. Returns a merged, indexed BufferGeometry in the animal's
 * own space: +Z is forward (the nose), the hooves sit on y = 0.
 */
export function horseGeometry() {
  return mergeAll(Object.values(horseParts()));
}

/**
 * The same animal, part by part, so a test can measure a landmark instead of
 * squinting at a merged bounding box. `horseGeometry()` is just these merged.
 */
export function horseParts() {
  const P = {};

  /* ---- barrel: dock (local y 0) to chest (local y 1.60) ---------------- *
   * The topline is not flat. The croup stands at 1.515, the loin dips to
   * 1.505, and the withers — the deepest ring, at the girth — is the high
   * point at 1.55, after which the chest falls away to 1.465. That dip and
   * rise is what separates a horse from a tube with legs. */
  const barrel = loft([
    { y: 0.00, rx: 0.150, rz: 0.185, sq: 2.7 },   // dock
    { y: 0.22, rx: 0.250, rz: 0.330, sq: 2.8 },   // croup
    { y: 0.55, rx: 0.265, rz: 0.320, sq: 2.7 },   // loin — the dip
    { y: 0.95, rx: 0.275, rz: 0.350, sq: 2.6 },   // ribs
    { y: 1.30, rx: 0.255, rz: 0.365, sq: 2.5 },   // girth / withers, deepest
    { y: BARREL_LEN, rx: 0.185, rz: 0.280, sq: 2.4 },  // chest front
  ], { seg: 12, vFromY: true, capTop: true, capBottom: true });
  // local +z is DOWN once laid over, so this darkens the underline and lifts
  // the topline — the one cue that stops a lofted tube reading as a barrel.
  paint(barrel, C.hide, (x, y, z) => (z > 0.16 ? K.belly : z < -0.18 ? K.back : 1));
  P.barrel = place(barrel, { rot: [Math.PI / 2, 0, 0], pos: [0, BARREL_Y, BARREL_Z] });

  /* ---- neck: steeply up out of the chest --------------------------------
   * tilt is FROM VERTICAL. 0.62 rad is 35° off vertical, i.e. carried at 55°
   * above horizontal, which is a warhorse's carriage rather than a grazing
   * one. The poll lands at [0, 1.93, 0.82]. */
  const neck = loft([
    { y: 0.00, rx: 0.190, rz: 0.250, sq: 2.5 },
    { y: 0.26, rx: 0.150, rz: 0.215, sq: 2.4 },
    { y: 0.50, rx: 0.122, rz: 0.185, sq: 2.3 },
    { y: NECK.len, rx: 0.100, rz: 0.150, sq: 2.2 },   // throatlatch
  ], { seg: 10, vFromY: true, capBottom: true });
  paint(neck, C.hide, (x, y, z) => (z < -0.10 ? K.back : z > 0.12 ? K.belly : 1));
  P.neck = place(neck, { rot: [NECK.tilt, 0, 0], pos: [0, NECK.y, NECK.z] });

  const pollY = POLL[1], pollZ = POLL[2];

  /* ---- head: dropped steeply off the poll -------------------------------
   * 2.53 rad from vertical is 55° BELOW horizontal, so the head makes a real
   * angle at the poll instead of continuing the neck. The muzzle finishes near
   * [0, 1.51, 1.11] — a touch under the withers, which is where an alert horse
   * carries it. */
  const head = loft([
    { y: 0.00, rx: 0.105, rz: 0.140, sq: 2.3 },   // poll
    { y: 0.13, rx: 0.112, rz: 0.168, sq: 2.2 },   // jowl — the widest point
    { y: 0.30, rx: 0.078, rz: 0.108, sq: 2.1 },   // nasal bone
    { y: 0.42, rx: 0.064, rz: 0.088, sq: 2.1 },
    { y: HEAD.len, rx: 0.066, rz: 0.082, sq: 2.3 },  // muzzle, flaring slightly
  ], { seg: 8, vFromY: true, capTop: true, capBottom: true });
  /* Three marks, and each earns its place at musou distance: a dark muzzle, a
   * pale blaze down the front of the face, and an eye. Local −z is the face
   * front once the head is tipped over; the eye sits on the side of the jowl. */
  paint(head, C.hide, (x, y, z) => {
    if (y > 0.44) return K.dark;                                   // muzzle
    if (y > 0.08 && y < 0.20 && Math.abs(x) > 0.075 && z < 0.02) return K.eye;
    if (z < -0.055 && Math.abs(x) < 0.040) return K.blaze;          // blaze
    return 1;
  });
  P.head = place(head, { rot: [HEAD.tilt, 0, 0], pos: [0, pollY, pollZ] });

  /* ---- ears: up and slightly back off the poll -------------------------- */
  for (const sx of [-1, 1]) {
    const ear = loft([
      { y: 0.00, rx: 0.032, rz: 0.024, sq: 2.0 },
      { y: 0.12, rx: 0.008, rz: 0.006, sq: 1.8 },
    ], { seg: 5, capTop: true });
    paint(ear, C.mane);
    P['ear' + sx] = place(ear, {
      rot: [-0.22, 0, sx * 0.26], pos: [sx * 0.052, pollY + 0.01, pollZ - 0.06],
    });
  }

  /* ---- legs ------------------------------------------------------------ *
   * Fore under the girth, hind under the croup, both narrow: seen head-on a
   * horse's legs are close together under a narrow chest. Each top clears the
   * 0.82 underline so the barrel swallows it and there is no seam. */
  for (const sx of [-1, 1]) {
    P['legF' + sx] = place(leg(true), { pos: [sx * 0.150, 0, 0.26] });
    P['legH' + sx] = place(leg(false), { pos: [sx * 0.168, 0, -0.80] });
  }

  /* ---- tail: off the croup, sweeping back and down --------------------- */
  const tail = loft([
    { y: 0.00, rx: 0.068, rz: 0.068, sq: 2.3 },
    { y: 0.26, rx: 0.060, rz: 0.060, sq: 2.1 },
    { y: 0.50, rx: 0.046, rz: 0.046, sq: 2.0 },
    { y: TAIL.len, rx: 0.018, rz: 0.018, sq: 2.0 },
  ], { seg: 6, vFromY: true, capTop: true });
  paint(tail, C.mane);
  P.tail = place(tail, { rot: [TAIL.tilt, 0, 0], pos: [0, TAIL.y, TAIL.z] });

  /* ---- mane: a crest of plates standing off the top of the neck --------- *
   * Anchored on the neck axis and pushed out along its perpendicular, so the
   * crest follows the neck instead of floating beside it however the neck is
   * tilted. */
  const ax = { y: Math.cos(NECK.tilt), z: Math.sin(NECK.tilt) };   // along the neck
  const up = { y: Math.sin(NECK.tilt), z: -Math.cos(NECK.tilt) };  // off its back
  for (let i = 0; i < 8; i++) {
    const t = i / 7;
    const r = 0.245 - t * 0.090;             // the neck's own half-depth here
    const h = 0.135 - t * 0.045;
    const crest = loft([
      { y: 0.00, rx: 0.017, rz: 0.058 - t * 0.016, sq: 2.0 },
      { y: h, rx: 0.008, rz: 0.038 - t * 0.013, sq: 1.8 },
    ], { seg: 4, capTop: true });
    paint(crest, C.mane);
    const s = t * NECK.len;
    P['mane' + i] = place(crest, {
      rot: [NECK.tilt - Math.PI / 2, 0, 0],
      pos: [0, NECK.y + s * ax.y + r * up.y, NECK.z + s * ax.z + r * up.z],
    });
  }

  /* ---- tack: blanket, saddle, breast strap ------------------------------ *
   * Tack is what says "ridden" rather than "wild", and at musou range it is
   * the only thing on the animal with a colour of its own. */
  const blanket = loft([
    { y: 0.00, rx: 0.286, rz: 0.372, sq: 2.6 },
    { y: 0.56, rx: 0.290, rz: 0.376, sq: 2.6 },
  ], { seg: 12, vFromY: true });
  paint(blanket, C.blanket, (x, y, z) => (z > 0.22 ? 0.78 : 1));
  P.blanket = place(blanket, { rot: [Math.PI / 2, 0, 0], pos: [0, BARREL_Y, -0.44] });

  /* The saddle is lofted UPRIGHT — one of the two things on this animal that
   * is genuinely vertical — and its seat lands on HORSE.seatY. */
  const saddle = loft([
    { y: 0.00, rx: 0.185, rz: 0.230, sq: 2.6 },
    { y: 0.12, rx: 0.205, rz: 0.250, sq: 2.5 },   // seat: HORSE.seatY
    { y: 0.22, rx: 0.140, rz: 0.180, sq: 2.4 },   // cantle
  ], { seg: 10, vFromY: true, capTop: true });
  paint(saddle, C.tack, (x, y) => (y > 0.14 ? K.trim : 1));
  P.saddle = place(saddle, { pos: [0, LANDMARKS.seat[1] - 0.12, LANDMARKS.seat[2]] });

  // breast strap: a band across the chest, which is what sells tack at range
  const strap = loft([
    { y: 0.00, rx: 0.212, rz: 0.306, sq: 2.4 },
    { y: 0.07, rx: 0.208, rz: 0.302, sq: 2.4 },
  ], { seg: 10, vFromY: true });
  paint(strap, C.tack);
  P.strap = place(strap, { rot: [Math.PI / 2, 0, 0], pos: [0, BARREL_Y + 0.02, 0.32] });

  return P;
}

/**
 * A ready-to-add horse mesh. The caller owns placement; the geometry's origin
 * is between the hooves, so `mesh.position` is a ground position and
 * `mesh.rotation.y` is the heading.
 */
export function makeHorse(material) {
  const g = horseGeometry();
  g.userData.tris = triCount(g);
  const m = new THREE.Mesh(g, material);
  m.castShadow = true;
  m.receiveShadow = true;
  m.name = 'horse';
  return m;
}
